import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import useAxios from 'axios-hooks';
import isObject from 'lodash.isobject';
import isNull from 'lodash.isnull';
import isNumber from 'lodash.isnumber';
import isEmpty from 'lodash.isempty';

import { Button } from '../../../../components';
import Question from './Question';

const QuestionnaireFormModal = ({ isOpen, onClose, userId, campaignPatient }) => {
	const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(0);
	const [answers, setAnswers] = useState({});
	const [showForm, setShowForm] = useState(false);

	const [{ data: existingQuestionnaires, loading, /* error */ }, getExistingDemographicData] = useAxios({
		method: 'GET',
		url: `/providers/${userId}/campaign-demographic-questionnaire`
	}, { manual: true });
	// const [{ data: groupData, loading: loadingGroup, /* error */ }, loadGroup] = useAxios({
	// 	// url: '/care_quest_groups',
	// 	method: 'GET',
	// }, { manual: true });
	const [{ data: qAndAData, loading: loadingQsAndAs, /* error */ }, loadQsAndAs] = useAxios({
		url: '/care_quest_questions',
		method: 'GET',
	}, { manual: true });
	const [{ loading: saving, /* error: sendError */ }, saveCompletedQuestionnaire] = useAxios({
		url: `/providers/${userId}/campaign-complete-assessment`,
		method: 'POST'
	}, { manual: true, autoCancel: false });

	useEffect(() => {
		setAnswers({});
		setSelectedQuestionnaire(0);
	}, [isOpen]);
	useEffect(() => {
		if (isOpen) {
			setSelectedQuestionnaire(0);
			getExistingDemographicData({ params: { campaign_demographic_id: campaignPatient.campaign_demographic_id } });
		}
	}, [isOpen, campaignPatient]);
	useEffect(() => {
		if (isOpen && selectedQuestionnaire > 0) {
			setAnswers({});
			// loadGroup({
			// 	url: `/care_quest_groups/${selectedQuestionnaire}`
			// });
			loadQsAndAs({
				params: {
					filter: JSON.stringify({
						where: {
							care_quest_groupId: selectedQuestionnaire
						},
						include: [{
							relation: 'care_quest_answers',
							order: ['answer_order ASC']
						}],
						order: ['question_order ASC']
					})
				}
			});
		}
	}, [selectedQuestionnaire, isOpen]);
	useEffect(() => {
		setShowForm(selectedQuestionnaire > 0);
	}, [selectedQuestionnaire]);
	useEffect(() => {
		if (Array.isArray(qAndAData)) {
			const defaultAnswers = {};
			qAndAData.forEach(question => {
				if (question.question_type === 'Multiple Choice Single Select') {
					defaultAnswers[`${question.id}`] = question.default_answer;
				} else if (question.question_type === 'Multiple Choice Multiple Select') {
					defaultAnswers[`${question.id}`] = [];
				} else if (question.question_type === 'Yes/No') {
					defaultAnswers[`${question.id}`] = question.default_answer;
				} else if (question.question_type === 'Freetext response') {
					defaultAnswers[`${question.id}`] = '';
				} else if (question.question_type.indexOf('PHR Link') === 0) {
					defaultAnswers[`${question.id}`] = '';
				} else if (question.question_type === 'Numeric') {
					defaultAnswers[`${question.id}`] = '';
				} else if (question.question_type === 'Wellness Scale') {
					defaultAnswers[`${question.id}`] = 0;
				} else if (question.question_type.indexOf('slider') === 0) {
					// set a default
					defaultAnswers[`${question.id}`] = 0;
					// convert from string or calculate default answer
					if (isNumber(question.default_answer)) {
						defaultAnswers[`${question.id}`] = parseInt(question.default_answer, 10);
					} else {
						try {
							const [, range] = question.question_type.split(':');
							const [min, max] = range.split('-');
							if (isNumber(min) && isNumber(max)) {
								defaultAnswers[`${question.id}`] = Math.round((parseInt(max, 10) - parseInt(min, 10)) / 2);
							}
						} catch (err) {
							// a default is set already
						}
					}
				} else {
					// TODO: other options as needed...
					// console.log('UNKNOWN', question)
				}
			});
			setAnswers(defaultAnswers);
		}
	}, [qAndAData]);

	const handleCancel = () => {
		if (showForm) {
			setSelectedQuestionnaire(0);
		} else {
			onClose();
		}
	};

	const handleAnswerValueChange = (questionId, answer) => {
		setAnswers({
			...answers,
			[`${questionId}`]: answer.answer_text ? answer.answer_text : answer
		});
	};

	const hideQuestion = (question) => {
		if (question && isNull(question.conditional_parent_question_id)) {
			return false;
		} else {
			const parentAnswer = answers[question.conditional_parent_question_id];
			if (!parentAnswer) {
				return false;
			}
			let conditionalPositiveResponse = question.conditional_positive_response;
			try {
				conditionalPositiveResponse = JSON.parse(conditionalPositiveResponse);
			} catch (err) {
				// console.error(err)
			}
			if (Array.isArray(conditionalPositiveResponse)) {
				return !conditionalPositiveResponse.includes(parentAnswer);
			} else if (isObject(conditionalPositiveResponse)) {
				const keys = Object.keys(conditionalPositiveResponse);
				if (keys.includes('lessThan') && keys.includes('greaterThan') && !isNaN(parseFloat(parentAnswer))) {
					const parentAnswerNum = parseFloat(parentAnswer);
					return parentAnswerNum > conditionalPositiveResponse.greaterThan && parentAnswerNum < conditionalPositiveResponse.lessThan;
				}
			} else { // string?
				return parentAnswer !== conditionalPositiveResponse;
			}
			return true;
		}
	};

	const handleSaveResponse = async () => {
		if (!showForm) {
			onClose();
			return;
		}
		const finalAnswers = [];
		const entities = qAndAData.reduce((prev, curr) => {
			prev[curr.id] = curr;
			return prev;
		}, {});
		Object.keys(answers).forEach(questionId => {
			if (hideQuestion(entities[questionId])) {
				return;
			}
			let isMultiChoice = false;
			const finalAnswer = {
				care_quest_questionId: parseInt(questionId),
				care_quest_groupId: entities[questionId].care_quest_groupId,
				response_dttm: new Date()
			};
			// extract answer id from choice questions
			if (['Multiple Choice Single Select', 'Yes/No', 'scored_slider'].includes(entities[questionId].question_type)) {
				const chosenAnswer = entities[questionId].care_quest_answers.find(answer => answer.answer_text === answers[questionId]);
				finalAnswer.care_quest_answerId = chosenAnswer.id;
			} else if (['Multiple Choice Multiple Select'].includes(entities[questionId].question_type)) {
				isMultiChoice = true;
				answers[questionId].forEach(response => {
					const chosenAnswer = entities[questionId].care_quest_answers.find(answer => answer.answer_text === response);
					const answer = {
						...finalAnswer,
						care_quest_answerId: chosenAnswer.id
					};
					finalAnswers.push(answer);
				});
			} else {
				finalAnswer.care_quest_answer_text = answers[questionId];
			}
			if (!isMultiChoice) {
				finalAnswers.push(finalAnswer);
			}
		});
		if (finalAnswers.length > 0) {
			await saveCompletedQuestionnaire({
				data: {
					campaign_demographic_id: campaignPatient.campaign_demographic_id,
					completed_assessment: {
						care_quest_groupId: selectedQuestionnaire,
						answers: finalAnswers
					}
				}
			});
			setSelectedQuestionnaire(0);
			getExistingDemographicData({ params: { campaign_demographic_id: campaignPatient.campaign_demographic_id } });
		}
	};

	return (
		<Modal isOpen={isOpen} contentLabel="Campaign Questionnaire Form" className="modal-dialog questionnaire-form-modal modal-lg">
			<div className="modal-content">
				<div className="modal-header">
					<div className="clearfix">
						<div className="pull-right">
							<button className="btn btn-link" onClick={onClose}>
								<em className="fa fa-lg fa-times" />
							</button>
						</div>
						<h3 className="modal-title">
							Patient Campaign Questionnaire
						</h3>
					</div>
				</div>
				<div className="modal-body">
					{showForm && (
						<div>
							{!loadingQsAndAs && !isEmpty(answers) && qAndAData && qAndAData.map((question) => (
								<React.Fragment key={`${question.id}`}>
									{!hideQuestion(question) && (
										<Question
											key={`${question.id}`}
											{...question}
											chosenAnswer={answers[`${question.id}`]}
											onAnswerValueChange={handleAnswerValueChange}
										/>
									)}
								</React.Fragment>
							))}
						</div>
					)}
					{!showForm && Array.isArray(existingQuestionnaires) && (
						<div>
							{existingQuestionnaires.filter(({ completed }) => completed == 0).map((questionnaire) => (
								<div key={`${questionnaire.questionnaire_id}`} className="mb-lg">
									<Button
										large
										primary
										title={questionnaire.name}
										wide
										onClick={() => setSelectedQuestionnaire(questionnaire.questionnaire_id)}
									/>
								</div>
							))}
							{existingQuestionnaires.filter(({ completed }) => completed > 0).map((questionnaire) => (
								<div key={`${questionnaire.questionnaire_id}`} className="mb-lg">
									<Button
										large
										title={questionnaire.name}
										wide
										disabled
									/>
								</div>
							))}
						</div>
					)}
					{/* {!loadingGroup && groupData && (
						<React.Fragment>
							<h4>Title</h4>
							<p>{groupData.name}</p>
							<h4>Header</h4>
							<p>{isEmpty(groupData.header_text) ? (
								<em>No header text</em>
							): groupData.header_text}</p>
						</React.Fragment>
					)} */}
				</div>
				<div className="modal-footer">
					<div className="pull-right">
						{showForm && (
							<Button
								title="Cancel"
								large
								onClick={handleCancel}
								disabled={loading || loadingQsAndAs || saving}
							/>
						)}
						<Button
							primary
							large
							title={showForm ? 'Save Questionnaire' : 'Close'}
							onClick={handleSaveResponse}
							disabled={loading || loadingQsAndAs || saving}
						/>
					</div>
				</div>
			</div>
		</Modal>
	);
};

QuestionnaireFormModal.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	campaignPatient: PropTypes.object,
	userId: PropTypes.number,
};

export default QuestionnaireFormModal;
